import { useContext } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, CheckCircle2, Shield } from "lucide-react";
import { RemoteBrowser } from "modules/cartCreation/components/RemoteBrowser";
import { useAccountLinkingRemoteBrowser } from "modules/checkout/hooks/useAccountLinkingRemoteBrowser";
import { StoreSessionStatus } from "modules/checkout/types/optimizedCart";
import { AppDataContext } from "modules/main/context/appData";

interface ConnectedAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disconnectedStores: StoreSessionStatus[];
  onCreateCarts?: () => void;
}

const getFallbackStoreName = (storeId: number): string => `Store ${storeId}`;

/**
 * Shows the stores that need account connection before cart creation can continue.
 */
export const ConnectedAccountsDialog = ({
  open,
  onOpenChange,
  disconnectedStores,
  onCreateCarts,
}: ConnectedAccountsDialogProps) => {
  const { storeList } = useContext(AppDataContext);
  const accountLinking = useAccountLinkingRemoteBrowser({ stores: disconnectedStores });

  const activeStore = accountLinking.remoteBrowserPayload
    ? storeList.find((item) => item.id === accountLinking.remoteBrowserPayload?.storeId)
    : undefined;
  const activeStoreName = activeStore?.name ?? (
    accountLinking.remoteBrowserPayload ? getFallbackStoreName(accountLinking.remoteBrowserPayload.storeId) : null
  );
  const isActionLocked = accountLinking.isOpening || accountLinking.isConfirming || accountLinking.isBrowserOpen;

  const handlePrimaryCtaClick = async (): Promise<void> => {
    if (accountLinking.canCreateCarts) {
      onCreateCarts?.();
      onOpenChange(false);
      return;
    }

    await accountLinking.startNextPendingStore();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Conecta tus cuentas de supermercado</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 leading-relaxed">
              <p>Para crear tus carritos, necesitamos conectar las cuentas de los supermercados seleccionados.</p>
              <div className="flex gap-2 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
                <Shield className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>
                  Al conectar una cuenta, abriremos un navegador seguro para que inicies sesión directamente en el supermercado. Carriapp no almacena tu contraseña.
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {disconnectedStores.map((status) => {
            const store = storeList.find((item) => item.id === status.store_id);
            const storeName = store?.name ?? getFallbackStoreName(status.store_id);
            const isLocallyConnected = status.connected || accountLinking.localConnectedStoreIds.includes(status.store_id);
            const isThisStoreOpening = accountLinking.isOpening && accountLinking.activeStoreId === status.store_id;
            const rowCtaLabel = isLocallyConnected ? "Conectado" : isThisStoreOpening ? "Abriendo..." : "Conectar";

            return (
              <div key={status.store_id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  {store?.logo_url ? (
                    <img
                      src={store.logo_url}
                      alt={`${storeName} logo`}
                      className="h-9 w-9 rounded-full object-contain"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                      {storeName.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{storeName}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      {isLocallyConnected && <CheckCircle2 className="h-3 w-3 text-primary" aria-hidden="true" />}
                      {isLocallyConnected ? "Cuenta conectada localmente" : "Cuenta no conectada"}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-label={`${rowCtaLabel} ${storeName}`}
                  disabled={isActionLocked || isLocallyConnected}
                  onClick={() => accountLinking.startLinking(status.store_id)}
                >
                  {rowCtaLabel}
                </Button>
              </div>
            );
          })}
        </div>
        {accountLinking.error && (
          <div className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>{accountLinking.error}</p>
          </div>
        )}
        <DialogFooter className="flex-col gap-3 sm:flex-col sm:space-x-0">
          <p className="text-xs text-muted-foreground">
            Por seguridad, tendrás que renovar esta conexión cada 30 días.
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Ahora no
            </Button>
            <Button type="button" disabled={isActionLocked && !accountLinking.canCreateCarts} onClick={handlePrimaryCtaClick}>
              {accountLinking.isOpening ? "Abriendo..." : accountLinking.primaryCtaLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      {accountLinking.remoteBrowserPayload && (
        <RemoteBrowser
          store_id={accountLinking.remoteBrowserPayload.storeId}
          storeName={activeStoreName}
          storeLogo={activeStore?.logo_url ?? null}
          currentStoreStatus="LOGIN_REQUIRED"
          token={accountLinking.remoteBrowserPayload.token}
          openState={accountLinking.isBrowserOpen}
          setOpen={(nextOpen) => {
            if (!nextOpen) {
              accountLinking.closeBrowser();
            }
          }}
          refreshToken={accountLinking.refreshActiveStoreToken}
          onConfirmSessionReady={() => {
            void accountLinking.confirmActiveStoreConnected();
          }}
        />
      )}
    </Dialog>
  );
};
